import {
  createReducer,
  on,
  Action,
  ActionReducerMap,
  combineReducers,
} from "@ngrx/store";
import { EntityState, EntityAdapter, createEntityAdapter } from "@ngrx/entity";

import * as GenericActions from "./acquisition-generic.actions";

export const ACQUISITION_GENERIC_FEATURE_KEY = "acquisitionGeneric";
export const ACQUISITION_GENERIC_PARCEL_FEATURE_KEY = "landParcel";
export const ACQUISITION_GENERIC_ADDRESS_BOOK_FEATURE_KEY = "incidentAddressBook";
export const ACQUISITION_GENERIC_TITLE_REGISTER_FEATURE_KEY =
  "landTitleRegister";
export const ACQUISITION_GENERIC_STORAGE_LOCATION_FEATURE_KEY =
  "storageLocation";
  export const ACQUISITION_GENERIC_ASSET_LOCATION_FEATURE_KEY =
  "assetLocation";
export const ACQUISITION_GENERIC_LESSOR_REPRESENTATIVE_FEATURE_KEY =
  "lessorRepresentative";
export const ACQUISITION_GENERIC_LESSORS_FEATURE_KEY = "lessors";
export const ACQUISITION_GENERIC_LESSOR_INFO_FEATURE_KEY = "lessorInfo";
export const ACQUISITION_GENERIC_LESSOR_AREA_MAKER_FEATURE_KEY = "lessorAreaMaker";
export const ACQUISITION_GENERIC_INDIVIDUAL_ACREAGES_FEATURE_KEY = "individualAcreages";
export const ACQUISITION_GENERIC_CREATED_USER_FEATURE_KEY = "createdUser";
export const ACQUISITION_GENERIC_MODIFY_USER_FEATURE_KEY = "modifyUser";

export function Generic_FeatureKey(key: string) {
  return key;
}

export enum UploadStatus {
  Ready = "Ready",
  Requested = "Requested",
  Started = "Started",
  Failed = "Failed",
  Completed = "Completed",
}

export interface GenericState extends EntityState<any> {
  selectedId?: string | number; // which Model Type record has been selected
  loaded: boolean; // has the Model list been loaded
  error?: string | null; // last none error (if any)
  status: UploadStatus;
  progress: number | null;
}

export interface GenericPartialState {
  readonly [ACQUISITION_GENERIC_FEATURE_KEY]: GenericState;
  readonly [ACQUISITION_GENERIC_PARCEL_FEATURE_KEY]: GenericState;
  readonly [ACQUISITION_GENERIC_ADDRESS_BOOK_FEATURE_KEY]: GenericState;
  readonly [ACQUISITION_GENERIC_TITLE_REGISTER_FEATURE_KEY]: GenericState;
  readonly [ACQUISITION_GENERIC_STORAGE_LOCATION_FEATURE_KEY]: GenericState;
  readonly [ACQUISITION_GENERIC_ASSET_LOCATION_FEATURE_KEY]: GenericState;
  readonly [ACQUISITION_GENERIC_LESSOR_REPRESENTATIVE_FEATURE_KEY]:
    GenericState;
  readonly [ACQUISITION_GENERIC_LESSORS_FEATURE_KEY]: GenericState;
  readonly [ACQUISITION_GENERIC_LESSOR_INFO_FEATURE_KEY]: GenericState;
  readonly [ACQUISITION_GENERIC_LESSOR_AREA_MAKER_FEATURE_KEY]: GenericState;
  readonly [ACQUISITION_GENERIC_INDIVIDUAL_ACREAGES_FEATURE_KEY]: GenericState;
  readonly [ACQUISITION_GENERIC_CREATED_USER_FEATURE_KEY]: GenericState;
  readonly [ACQUISITION_GENERIC_MODIFY_USER_FEATURE_KEY]: GenericState;
}

export const GenericAdapter: EntityAdapter<any> = createEntityAdapter<
  any
>({
  selectId: (generic) => generic.id,
  sortComparer: false,
});

export const initialState: GenericState = GenericAdapter.getInitialState({
  // set initial required properties
  loaded: false,
  error: null,
  selectedId: null,
  status: UploadStatus.Ready,
  progress: null,
});

export const GenericReducer = (type: string) =>
  createReducer(
    initialState,
    on(GenericActions.loadIncidenceGeneric(type), (state) => ({
      ...state,
      loaded: true,
      error: null,
    })),
    on(GenericActions.loadIncidenceGenericSuccess(type), (state, { returnedData }) =>
      GenericAdapter.setAll(returnedData, { ...state, loaded: false })),
    on(GenericActions.loadIncidenceGenericFailure(type), (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })),
    on(GenericActions.loadIncidenceGenericWithoutId(type), (state) => ({
      ...state,
      loaded: true,
      error: null,
    })),
    on(GenericActions.loadIncidenceGenericWithoutIdSuccess(type), (state, { returnedData }) =>
      GenericAdapter.setAll(returnedData, { ...state, loaded: false })),
    on(GenericActions.loadIncidenceGenericWithoutIdFailure(type), (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })),
    on(GenericActions.loadIncidenceByIdGeneric(type), (state) => ({
      ...state,
      loaded: true,
      error: null,
    })),
    on(GenericActions.loadIncidenceByIdGenericSuccess(type), (state, { returnedData }) =>
      GenericAdapter.setAll(returnedData, { ...state, loaded: false })),
    on(GenericActions.loadIncidenceByIdGenericFailure(type), (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })),
    on(GenericActions.addIncidenceGeneric(type), (state) => ({
      ...state,
      loaded: true,
      error: null,
    })),
    on(GenericActions.addIncidenceGenericSuccess(type), (state, { returnedData }) =>
      GenericAdapter.upsertMany(returnedData, { ...state, loaded: false })),
    on(GenericActions.addIncidenceGenericFailure(type), (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })),
    on(GenericActions.updateIncidenceGeneric(type), (state) => ({
      ...state,
      loaded: true,
      error: null,
    })),
    on(GenericActions.updateIncidenceGenericSuccess(type), (state, { returnedData }) =>
      GenericAdapter.upsertMany(returnedData, { ...state, loaded: false })),
    on(GenericActions.updateIncidenceGenericFailure(type), (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })),
    on(GenericActions.removeIncidenceGeneric(type), (state, inputtype) => ({
      ...state,
      loaded: true,
      error: null,
    })),
    on(GenericActions.removeIncidenceGenericSuccess(type), (state, { ids }) =>
      GenericAdapter.removeMany(ids, { ...state, loaded: false })),
    on(GenericActions.removeIncidenceGenericFailure(type), (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })),
    on(GenericActions.loadIncidenceGenericClearState(type), (state) => ({
      ...state,
      loaded: true,
      error: null,
    })),
    on(GenericActions.loadIncidenceGenericClearStateSuccess(type), (state, { returnedData }) =>
      GenericAdapter.setAll(returnedData, { ...state, loaded: false })),

  );

export const reducer = (state: GenericState | undefined, action: Action) =>
  GenericReducer("Country")(state, action);

export const landParcelReducer = (
  state: GenericState | undefined,
  action: Action,
) => GenericReducer("LandParcel")(state, action);

export const addressBookReducer = (
  state: GenericState | undefined,
  action: Action,
) => GenericReducer("AddressBook")(state, action);

export const landTitleRegisterReducer = (
  state: GenericState | undefined,
  action: Action,
) => GenericReducer("LandTitleRegister")(state, action);

export const storageLocationReducer = (
  state: GenericState | undefined,
  action: Action,
) => GenericReducer("StorageLocation")(state, action);

export const assetLocationReducer = (
  state: GenericState | undefined,
  action: Action,
) => GenericReducer("AssetLocation")(state, action);

export const lessorRepresentativeReducer = (
  state: GenericState | undefined,
  action: Action,
) => GenericReducer("LessorRepresentative")(state, action);

export const lessorsReducer = (
  state: GenericState | undefined,
  action: Action,
) => GenericReducer("Lessors")(state, action);

export const lessorInfoReducer = (
  state: GenericState | undefined,
  action: Action,
) => GenericReducer("LessorInfo")(state, action);

export const lessorAreaMakerReducer = (
  state: GenericState | undefined,
  action: Action,
) => GenericReducer("LessorAreaMaker")(state, action);

export const individualAcreagesReducer = (
  state: GenericState | undefined,
  action: Action,
) => GenericReducer("IndividualAcreages")(state, action);

export const createdUserReducer = (
  state: GenericState | undefined,
  action: Action,
) => GenericReducer("CreatedUser")(state, action);

export const modifyUserReducer = (
  state: GenericState | undefined,
  action: Action,
) => GenericReducer("ModifyUser")(state, action);

// export const rootReducer = combineReducers({
//   "landParcel": GenericReducer("LandParcel"),
//   "acquisitionGeneric": GenericReducer("Country"),
// });
